import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";

export interface TenantSubscription {
  tenant_subscription_id: number;
  subscription_display_name: string;
  max_user: number | null;
  amount: number | null;
  interval_length: string;
  is_trial: boolean;
  interval_display_name: string;
  interval_key: string;
  interval_id: string;
  tenant_id: string;
  tenant_name?: string;
  domain_name?: string;
  tenant?: {
    tenant_name: string;
    domain_name: string;
  };
  time_zone: string | null;
  language: string | null;
  currency_code: string | null;
  created_date: string;
  updated_date: string;
  subscription_start_date: string;
  subscription_end_date: string;
  is_active: boolean;
  is_expired: boolean;
  created_datetime: string;
  updated_datetime: string;
  number_of_user: number;
  subscription_id: number;
}

export interface SubscriptionResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TenantSubscription[];
}

export interface SubscriptionRequest extends Partial<TenantSubscription> {}

export class SubscriptionService {
  private static baseUrl = `${API_BASE_URL}/tenant-subscription/`;

  static async getSubscriptions(
    page: number = 1,
    pageSize: number = 10,
    params?: Record<string, any>
  ): Promise<SubscriptionResponse> {
    try {
      const headers = getAuthHeaders();
      const urlParams = new URLSearchParams();
      urlParams.set("page", page.toString());
      urlParams.set("page_size", pageSize.toString());

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            urlParams.set(key, value.toString());
          }
        });
      }

      const url = `${this.baseUrl}?${urlParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      throw error;
    }
  }

  static async getSubscription(id: number): Promise<TenantSubscription> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${id}/`, {
        method: "GET",
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching subscription ${id}:`, error);
      throw error;
    }
  }

  static async createSubscription(
    subscription: SubscriptionRequest
  ): Promise<TenantSubscription> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(this.baseUrl, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(subscription),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating subscription:", error);
      throw error;
    }
  }

  static async updateSubscription(
    id: number,
    subscription: SubscriptionRequest
  ): Promise<TenantSubscription> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${id}/`, {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(subscription),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating subscription ${id}:`, error);
      throw error;
    }
  }

  static async deleteSubscription(id: number): Promise<void> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${id}/`, {
        method: "DELETE",
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error deleting subscription ${id}:`, error);
      throw error;
    }
  }
}
